import { useAuth } from '@clerk/clerk-react';
import { createFileRoute, notFound, useNavigate } from '@tanstack/react-router';
import CreatePortfolioCompanyContainer from '~/components/ui/company/CreatePortfolioCompanyContainer/CreatePortfolioCompanyContainer';

import { Breadcrumb } from '~/components/ui/layout/navigation/breadcrumb/Breadcrumb';
import { HeaderContainer } from '~/components/ui/layout/page/HeaderContainer';
import { PageContent } from '~/components/ui/layout/page/PageContent';
import { PageTagline } from '~/components/ui/layout/page/PageTagline';
import { z } from 'zod';
import { getPortfolioLinkParams } from '~/components/ui/tables/ag/portfolios/PortfolioLinkCell';
import { canAccess } from '~/utils/rbac';
import { useRequireFeatureFlag } from '~/hooks/useRequireFeatureFlag';
import { FEATURE_FLAGS } from '~/config/orgConfigConstants';

const companiesListSearchSchema = z.object({
  //fund name is used for path - the portfolio company name is used for the breadcrumb
  fundName: z.string().optional(),
  //generally we should be in context a fund here
  //optimally we should extract fund ID fund name from portfolio data
  fundId: z.string().optional(),
});

const PortfolioCreateRoute = () => {
  const { allowed } = useRequireFeatureFlag(FEATURE_FLAGS.PORTFOLIO_MANAGEMENT);
  if (!allowed) return null;
  return <PortfolioCreate />;
};

export const Route = createFileRoute('/_authLayout/portfolio/create')({
  beforeLoad: async () => {
    if (!canAccess('portfolio')) {
      throw notFound();
    }
  },
  component: PortfolioCreateRoute,
  validateSearch: companiesListSearchSchema,
});

export const PortfolioCreate = () => {
  const auth = useAuth();
  const { fundName, fundId } = Route.useSearch();
  const navigate = useNavigate();

  if (!auth.isSignedIn && auth.isLoaded) {
    navigate({ to: '/sign-in' });
  }
  return (
    <>
      <HeaderContainer>
        <Breadcrumb
          items={[
            { name: 'Funds', url: `/fund/overview` },
            {
              name: 'Portfolio Companies',
              url: `/fund/portfolioCompanies/${fundId}${getPortfolioLinkParams({ fundId, fundName })}`,
            },
            { name: 'Create Company' },
          ]}
        />
        <PageTagline message="Create a portfolio company" />
      </HeaderContainer>
      <PageContent>
        <CreatePortfolioCompanyContainer />
      </PageContent>
    </>
  );
};
